import {Localizer} from "../../../infrastructure/localization/localizer";
import React, {ReactFragment, useEffect, useState} from "react";
import {HttpClientService} from "../../../services/httpClient/httpClientService";
import {AccountService} from "../../../services/account/accountService";
import {Loading} from "../loading/loading";
import {ForloebOverviewConstants} from "./forloebOverviewConstants";
import {LaegeStillingView} from "./stillingViewComponents/laegeStillingView";
import {UddannelsesstedView} from "./stillingViewComponents/uddannelsesstedView";
import {ForloebApi} from "../../../services/api/forloeb/forloebApi";
import {ForloebOverviewFilterModel} from "./ForloebOverviewFilterModel";
import {FilterParamsModel} from "../../sharedmodels/filter/FilterParamsModel";
import {PaginationForOverview} from "./stillingViewComponents/paginationForOverview";
import {useAppSelector} from "../../../app/hooks";
import {forloebOverviewFiltersReducer, ForloebOverviewFiltersState} from "./forloebOverviewFiltersSlice";
import {
    calculateAndSetYearsForStillinger,
    cloneFilterFromState,
    overlappingCount,
    overlappingDates
} from "./forloebOverviewUtils/forloebOverviewUtils";
import {resetScrollToYearInForloebOverviewChart, scrollToYearInForloebOverviewChart} from "./scrollHelper";
import {ForloebOverviewNoFilterResults} from "./forloebOverviewNoFilterResults";
import {
    getDateFilterInput,
    getFilterEndDate,
    getFilterStartDate,
    getScrollForloebDateChartInput
} from "./ForloebOverviewDateFilterModel";
import Overview from "./OverviewComponent/Overview";
import ForloebOverviewcontainer from "./forloebOverviewContainer";
import {HorizontalLineSpacer} from "./OverviewComponent/horizontalLineSpacer";
import {toDate} from "../../../index";
import {HorizontalLine} from "./OverviewComponent/horizontalLine";
import {HoverLine} from "./OverviewComponent/hoverLine";
import useScreenResolution from "core/hooks/useScreenResolution";
import {UddannelsesstedForloebKladderModel} from "../../sharedmodels/forloeb/uddannelsesstedForloebKladderModel";
import {StillingForloebKladderOverviewModel} from "../../sharedmodels/stilling/stillingForloebKladderOverviewModel";
import {ForloebKladdeModel} from "../../sharedmodels/stilling/forloebKladdeModel";
import {StartAndEndDateModel} from "../../sharedmodels/forloeb/startAndEndDateModel";
import {SlideIn} from "../slideIn/slideIn";
import {CreateForloebFromKladde} from "../../componentsPage/forloebKladde/createForloebFromKladde";
import useUser from "../../hooks/useUser";

type BaseLookupStateModel = {
    rowIndex: number;
    collapsed: boolean;
}

type UddannelsesstedLookupStateModel = {
    uddannelsesstedId: string;
    uddannelsesstedNavn: string;
} & BaseLookupStateModel;

type AfdelingLookupStateModel = {
    afdelingId: string;
    afdelingNavn: string;
    uddannelsesstedId: string;
} & BaseLookupStateModel;

type ForloebKladdeLookupStateModel = {
    forloebKladdeModel: StillingForloebKladderOverviewModel;
    uddannelsesstedId: string;
    afdelingId?: string;
} & BaseLookupStateModel;

export interface ForloebOverviewKladdeViewProps {
    overviewFilter: ForloebOverviewFilterModel;
    filterParams: FilterParamsModel;
    handlePaginationCallback: (page: number) => void;
    setPaginationCountCallback: (filteredRecords: number, recordsTotal: number, xOfYisLoading: boolean) => ReactFragment;
    handleForloebClicked: (forloebId: string) => void;
    scrollContainerRef: React.RefObject<HTMLDivElement>;
    overlappendeVisningState?: boolean;
    visHorizontaleLinjerState?: boolean;
}

const forloebApi = new ForloebApi(new HttpClientService(), new AccountService());

export const ForloebOverviewKladdeView = ({
                                              overviewFilter,
                                              filterParams,
                                              handlePaginationCallback,
                                              setPaginationCountCallback,
                                              scrollContainerRef,
                                              overlappendeVisningState = true,
                                              visHorizontaleLinjerState = true
                                          }: ForloebOverviewKladdeViewProps) => {

    const forloebOverviewFilterSliceState = useAppSelector(forloebOverviewFiltersReducer) as ForloebOverviewFiltersState;

    //constants
    const months = ForloebOverviewConstants.months;
    const offset = 2;

    //states
    const [isLoading, setIsLoading] = useState(true);
    const [uddannelsesstederWithStillinger, setUddannelsesstederWithStillinger] = useState([] as UddannelsesstedForloebKladderModel[]);
    const [uddannelsesstederLookupState, setUddannelsesstederLookupState] = useState<UddannelsesstedLookupStateModel[]>([]);
    const [afdelingerLookupState, setAfdelingerLookupState] = useState<AfdelingLookupStateModel[]>([]);
    const [forloebKladdeLookupState, setForloebKladdeLookupState] = useState<ForloebKladdeLookupStateModel[]>([]);
    const [rowCountState, setRowCountState] = useState<number>(1)
    const [filteredRecords, setFilteredRecords] = useState<number>(0);
    const [recordsTotal, setRecordsTotal] = useState<number>(0);
    const [missingIndexesState, setMissingIndexesState] = useState<number[]>([]);
    const [years, setYears] = useState(ForloebOverviewConstants.years as number[]);
    const [xOfYisLoading, setXOfYisLoading] = useState(true)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [forloebKladdeClicked, setForloebKladdeClicked] = useState<ForloebKladdeModel>()

    const { currentUser } = useUser();
    const currentUserCanEditPlanlagtForloeb = currentUser.IsAdminOrVus() || currentUser.IsHrMedarbejder();

    const {isMobile} = useScreenResolution();
    const editmodalId = "editForloebModal";


    useEffect(() => {
        const dateFilterInput = getDateFilterInput(forloebOverviewFilterSliceState);
        const inputModel = getScrollForloebDateChartInput(dateFilterInput, years[0], scrollContainerRef);

        if (!isLoading) {
            scrollToYearInForloebOverviewChart(inputModel);
        } else {
            resetScrollToYearInForloebOverviewChart(inputModel);
        }

    }, [isMobile, forloebOverviewFilterSliceState, isLoading, scrollContainerRef, years]);

    useEffect(() => {
        setIsLoading(true);
        setRowCountState(1);
        const filterClone = cloneFilterFromState(forloebOverviewFilterSliceState, overviewFilter);
        const fetchForloebKladderAsync = async () => {
            const result = await forloebApi.getLaegeinstitutionerWithForloebKladderAsyncWithCancellation(filterClone, filterParams);
            setUddannelsesstederWithStillinger(result.data);

            const startYear = filterClone.year;

            if (startYear !== 0) {
                const allForloebKladdeCompareModels = result.data.flatMap(x => x.GetAllForloebCompareModels());
                const calculatedYears = calculateAndSetYearsForStillinger(Number(startYear), allForloebKladdeCompareModels);
                setYears(calculatedYears);
            }

            setIsLoading(false);
        }
        fetchForloebKladderAsync();

        const fetchXofY = async () => {
            setXOfYisLoading(true);
            const XofY = await forloebApi.getXofYLaegeinstitutionerWithKladdeForloeb(filterClone, filterParams);
            setFilteredRecords(XofY.recordsFiltered);
            setRecordsTotal(XofY.recordsTotal);
        }
        fetchXofY().then(() => setXOfYisLoading(false));

        return () => {
            forloebApi.cancelPendingRequests();
        }
    }, [forloebOverviewFilterSliceState, overviewFilter, filterParams]);

    useEffect(() => {
        const getUddannelsesstederWithStillingerToRender = () => {
            let missingIndexes: number[] = [];

            let uddannelsesstederLookup: UddannelsesstedLookupStateModel[] = [];
            let afdelingerLookup: AfdelingLookupStateModel[] = [];
            let stillingerLookup: ForloebKladdeLookupStateModel[] = [];

            let rowCount = offset; //offset på 2
            function setMissingIndexesAndReturnAntalOverlap(stilling: StillingForloebKladderOverviewModel, index: number) {
                const antalOverlap = overlappingCount(stilling.forloebKladde)
                if (antalOverlap > 0) {
                    for (let i = 1; i <= antalOverlap; i++) {
                        missingIndexes.push(index + i);
                    }
                }
                return antalOverlap;
            }

            uddannelsesstederWithStillinger.forEach((uddannelsesstedWithStillinger, index) => {
                ++rowCount;
                uddannelsesstederLookup.push({
                    uddannelsesstedId: uddannelsesstedWithStillinger.uddannelsesstedId,
                    uddannelsesstedNavn: uddannelsesstedWithStillinger.uddannelsesstedNavn,
                    rowIndex: rowCount,
                    collapsed: false
                });
                uddannelsesstedWithStillinger.afdelingStillinger.forEach((afdelingStilling) => {
                    ++rowCount;
                    afdelingerLookup.push({
                        afdelingId: afdelingStilling.afdelingId,
                        afdelingNavn: afdelingStilling.afdelingNavn,
                        uddannelsesstedId: uddannelsesstedWithStillinger.uddannelsesstedId,
                        rowIndex: rowCount,
                        collapsed: false
                    });

                    afdelingStilling.afdelingStillingForloebKladder.forEach((forloebKladde) => {
                        ++rowCount;
                        stillingerLookup.push({
                            forloebKladdeModel: forloebKladde,
                            rowIndex: rowCount,
                            uddannelsesstedId: uddannelsesstedWithStillinger.uddannelsesstedId,
                            afdelingId: afdelingStilling.afdelingId,
                            collapsed: false
                        });
                        if (overlappendeVisningState) {
                            rowCount += setMissingIndexesAndReturnAntalOverlap(forloebKladde, rowCount);
                        }
                    });
                });

                uddannelsesstedWithStillinger.praksisStillingerForloebKladder.forEach((forloebKladde) => {
                    ++rowCount;
                    stillingerLookup.push({
                        forloebKladdeModel: forloebKladde,
                        rowIndex: rowCount,
                        uddannelsesstedId: uddannelsesstedWithStillinger.uddannelsesstedId,
                        collapsed: false
                    });
                    if (overlappendeVisningState) {
                        rowCount += setMissingIndexesAndReturnAntalOverlap(forloebKladde, rowCount);
                    }
                })
            });

            setUddannelsesstederLookupState(uddannelsesstederLookup);
            setAfdelingerLookupState(afdelingerLookup);
            setForloebKladdeLookupState(stillingerLookup);
            setMissingIndexesState(missingIndexes)
        }


        getUddannelsesstederWithStillingerToRender();
    }, [overlappendeVisningState, uddannelsesstederWithStillinger])

    const renderEmptyStillingBackground = () => {
        const rows: JSX.Element[] = [];
        for (let missingIndex of missingIndexesState) {
            rows.push(
                <div key={missingIndex} className={`stilling-header`}
                     style={{"--uddannelsessted-row-index": missingIndex} as React.CSSProperties}>
                </div>
            );
        }
        return rows;
    }

    useEffect(() => {

        let rowCount = (uddannelsesstederLookupState.length
            + afdelingerLookupState.length
            + forloebKladdeLookupState.length) + 1; // Vi +'er 1, for at rendere hvid background

        if (overlappendeVisningState) {
            const praksisForloebKladdeStillinger = uddannelsesstederWithStillinger.flatMap(x => x.praksisStillingerForloebKladder).flatMap(x => x.forloebKladde);
            const afdelingForloebKladdeStillinger = uddannelsesstederWithStillinger.flatMap(x => x.afdelingStillinger).flatMap(x => x.afdelingStillingForloebKladder).flatMap(x => x.forloebKladde);
            const laegestillinger = praksisForloebKladdeStillinger.concat(afdelingForloebKladdeStillinger);

            const amountOfOverlappingForloeb = overlappingCount(laegestillinger);
            rowCount += amountOfOverlappingForloeb;
        }

        setRowCountState(rowCount)
    }, [afdelingerLookupState, overlappendeVisningState, forloebKladdeLookupState, uddannelsesstederLookupState, uddannelsesstederWithStillinger])

    const openEditForloebModel = (forloebKladde: ForloebKladdeModel) => {
        if (currentUserCanEditPlanlagtForloeb) {
            setEditModalOpen(true);
            setForloebKladdeClicked(forloebKladde);
        }
    }

    return (
        <ForloebOverviewcontainer>
            <div style={
                {
                    "--total-month-count": years.length * months.length,
                    "--month-count": months.length,
                    "--total-row-count": rowCountState,
                } as React.CSSProperties}>
                <div className="forloeb-chart" ref={scrollContainerRef}>
                    <div className="laege-header flex-column align-items-start">
                        <h5>{Localizer.global_stillinger()}</h5>
                        <div>{setPaginationCountCallback(filteredRecords, recordsTotal, xOfYisLoading)}</div>
                    </div>
                    {isLoading &&
                        <>
                            <div className={"forloeb-list-empty-state"}>
                                <Loading isLoading={isLoading} text={Localizer.forloebOverviewPage_getStillinger()}>
                                </Loading>
                            </div>
                            <HorizontalLineSpacer/>
                            <PaginationForOverview
                                filterParams={filterParams}
                                maxRowIndex={rowCountState}
                                filteredRecords={filteredRecords}
                                handlePaginationCallback={(page: number) => handlePaginationCallback(page)}/>
                        </>
                    }

                    {!isLoading &&
                        <>
                            {uddannelsesstederWithStillinger.length > 0 ?
                                <Overview>
                                    <Overview.YearsHeader years={years}/>

                                    <Overview.VerticalLines
                                        years={years}
                                        rowSize={rowCountState}
                                        startDate={getFilterStartDate(getDateFilterInput(forloebOverviewFilterSliceState))}
                                        endDate={getFilterEndDate(getDateFilterInput(forloebOverviewFilterSliceState))}
                                        dateFilterType={forloebOverviewFilterSliceState.dateFilterType}
                                    />

                                    {overlappendeVisningState &&
                                        renderEmptyStillingBackground()
                                    }

                                    <Overview.HorizontalLines>
                                        {uddannelsesstederLookupState.length > 0 &&
                                            uddannelsesstederLookupState.map(uddannelsessted =>
                                                <UddannelsesstedView
                                                    key={`view-${uddannelsessted.rowIndex}-${uddannelsessted.uddannelsesstedId}`}
                                                    uddannelsesstedLookUp={uddannelsessted}
                                                    afdelinger={afdelingerLookupState.filter(a => a.uddannelsesstedId === uddannelsessted.uddannelsesstedId)}
                                                    stillinger={forloebKladdeLookupState
                                                        .filter(s => s.uddannelsesstedId === uddannelsessted.uddannelsesstedId)
                                                        .map(s => ({
                                                            stillingId: s.forloebKladdeModel.stillingId,
                                                            stillingNummer: s.forloebKladdeModel.stillingNummer,
                                                            rowIndex: s.rowIndex,
                                                            uddannelsesstedId: s.uddannelsesstedId,
                                                            afdelingId: s.afdelingId,
                                                            collapsed: s.collapsed
                                                        }))}
                                                    years={years}
                                                    visHorizontaleLinjerState={visHorizontaleLinjerState}
                                                />
                                            )
                                        }

                                        {forloebKladdeLookupState.length > 0 && visHorizontaleLinjerState &&
                                            forloebKladdeLookupState.filter(a => !a.collapsed).map((stilling) => {
                                                    return <HorizontalLine
                                                        key={`stilling-${stilling.rowIndex}-${stilling.forloebKladdeModel.stillingId}`}
                                                        rowIndex={stilling.rowIndex}
                                                        columnStart={ForloebOverviewConstants.timelineColumnStart}
                                                        columnEnd={ForloebOverviewConstants.months.length * toDate(forloebOverviewFilterSliceState.periodTo).getFullYear()}/>
                                                }
                                            )
                                        }

                                        {afdelingerLookupState.length > 0 && visHorizontaleLinjerState &&
                                            afdelingerLookupState.map((afdeling) => {
                                                    return <HorizontalLine
                                                        key={`afdeling-${afdeling.rowIndex}-${afdeling.afdelingId}`}
                                                        rowIndex={afdeling.rowIndex}
                                                        columnStart={ForloebOverviewConstants.timelineColumnStart}
                                                        columnEnd={ForloebOverviewConstants.months.length * toDate(forloebOverviewFilterSliceState.periodTo).getFullYear()}
                                                        rowColor={ForloebOverviewConstants.afdelingHorizontalLinesBackgroundColor}
                                                    />
                                                }
                                            )
                                        }

                                        {uddannelsesstederLookupState.length > 0 && visHorizontaleLinjerState &&
                                            uddannelsesstederLookupState.map((uddannelsessted) => {
                                                    return <HorizontalLine
                                                        key={`horizontalline-${uddannelsessted.rowIndex}-${uddannelsessted.uddannelsesstedId}`}
                                                        rowIndex={uddannelsessted.rowIndex}
                                                        columnStart={ForloebOverviewConstants.timelineColumnStart}
                                                        columnEnd={ForloebOverviewConstants.months.length * toDate(forloebOverviewFilterSliceState.periodTo).getFullYear()}
                                                        rowColor={ForloebOverviewConstants.uddannelsesstedHorizontalLinesBackgroundColor}
                                                    />
                                                }
                                            )
                                        }


                                        {forloebKladdeLookupState.length > 0 &&
                                            forloebKladdeLookupState.filter(a => !a.collapsed).map((stilling) => {
                                                    let rowindex = stilling.rowIndex;
                                                    return (
                                                        stilling.forloebKladdeModel.forloebKladde
                                                            .sort((a, b) => a.endDate > b.endDate ? 1 : -1)
                                                            .sort((a, b) => a.stillingId.localeCompare(b.stillingId))
                                                            .map((fk, index) => {
                                                                    const previousForloebKladde = index > 0 ? stilling.forloebKladdeModel.forloebKladde[index - 1] : undefined;
                                                                    const anyOverlappingDates = overlappingDates(fk, previousForloebKladde);
                                                                    const rowindexCurrent = overlappendeVisningState
                                                                        ? anyOverlappingDates ? ++rowindex : rowindex
                                                                        : stilling.rowIndex
                                                                    return (
                                                                        <React.Fragment
                                                                            key={`${stilling.forloebKladdeModel.stillingId}-${fk.forloebKladdeId}`}>
                                                                            <LaegeStillingView
                                                                                rowIndex={rowindexCurrent}
                                                                                forloebKladde={fk}
                                                                                years={years}
                                                                                excludeClickEvent={!currentUserCanEditPlanlagtForloeb}
                                                                                handleForloebClicked={() => openEditForloebModel(fk)}
                                                                                startDate={fk.startDate}
                                                                                endDate={fk.endDate}
                                                                                elementId={fk.forloebKladdeId}
                                                                            />
                                                                            {visHorizontaleLinjerState &&
                                                                                <HoverLine
                                                                                    columnStart={ForloebOverviewConstants.timelineColumnStart}
                                                                                    columnEnd={ForloebOverviewConstants.months.length * toDate(forloebOverviewFilterSliceState.periodTo).getFullYear()}
                                                                                    rowColor={ForloebOverviewConstants.uddannelsesstedHorizontalLinesBackgroundColor}
                                                                                    rowIndex={rowindexCurrent}
                                                                                />
                                                                            }
                                                                        </React.Fragment>
                                                                    )
                                                                }
                                                            )
                                                    )
                                                }
                                            )
                                        }
                                    </Overview.HorizontalLines>
                                </Overview>
                                :
                                <>
                                    <HorizontalLineSpacer/>
                                    <ForloebOverviewNoFilterResults/>
                                </>

                            }
                            <PaginationForOverview
                                filterParams={filterParams}
                                maxRowIndex={rowCountState}
                                filteredRecords={filteredRecords}
                                handlePaginationCallback={(page: number) => handlePaginationCallback(page)}/>
                        </>
                    }
                </div>
            </div>
            {editModalOpen && forloebKladdeClicked &&
                <SlideIn
                    id={editmodalId}
                    title={Localizer.forloebpage_redigerPlanlagtForloeb()}
                    actionText={Localizer.global_creating()}
                    actionOnCloseCallback={() => {setEditModalOpen(false)}}
                    actionFinishedCallback={() => {setEditModalOpen(false)}}
                    defaultOpen={true}
                    bodyContent={<CreateForloebFromKladde
                        modalId={editmodalId}
                        model={forloebKladdeClicked}
                    />}
                />
            }
        </ForloebOverviewcontainer>
    )
}
